import * as React from 'react';

import {SelectTheme} from 'common/components/select/constants';
import {SelectReact} from 'common/components/select';
import {isCohortSegment, SegmentValue} from 'common/query-builder/query-block/segment-menu/types';
import {
  isDateRangeProfileHistoryOptions,
  ProfileHistoryType,
  ProfileHistoryTypeToDisplayName,
  Segmentation,
} from 'common/types/reports/query';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {SelectAttrs} from 'common/components/select/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {QueryBlockReact} from 'common/query-builder/query-block';
import {MenuType} from 'common/query-builder/query-block/types';

import {ControlBarAction, ControlBarActionItems} from '../constants';
import {SelectorTypeDefinitions} from '../types';

/**
 * Renders profile history controls for a given Segmentation entry on filters or breakdowns
 */
export function ProfileHistorySubItem(props: {
  value: SegmentValue;
  onTypeChange: (newValue: SegmentValue) => void;
  hasNewUI: boolean;
  hideEvents: boolean;
  selectorType: SelectorTypeDefinitions;
}) {
  if (
    !props.value ||
    isCohortSegment(props.value) ||
    ![SelectorTypeDefinitions.Filter, SelectorTypeDefinitions.Segment].includes(props.selectorType)
  ) {
    return null;
  }

  const segmentation = props.value as Segmentation;
  const currentType = segmentation.profileHistoryOptions?.type ?? ProfileHistoryType.Latest;

  // Render a date selector to specify the time range
  if (isDateRangeProfileHistoryOptions(segmentation.profileHistoryOptions)) {
    return (
      <div className='subitem-text'>
        <QueryBlockReact
          className='subitem-style'
          type={MenuType.Time}
          theme={props.hasNewUI ? ButtonTheme.QbSubitem : ButtonTheme.Select}
          buttonSize={props.hasNewUI ? ButtonSize.Small : ButtonSize.Medium}
          selected={segmentation.profileHistoryOptions.dateRange}
          selectorIcon='calendar'
          onSelect={(selection: DateRange) => {
            props.onTypeChange({
              ...segmentation,
              profileHistoryOptions: {
                type: ProfileHistoryType.DateRange,
                dateRange: selection.getAttributes(),
              },
            });
          }}
        />
      </div>
    );
  }

  const displayName = ProfileHistoryTypeToDisplayName[currentType];
  let displaySpecificAttrs: Partial<SelectAttrs>;
  if (props.hasNewUI) {
    displaySpecificAttrs = {
      theme: SelectTheme.QbSubitem,
      label: `Profile history type changed to **${displayName}**`,
      size: ButtonSize.Small,
    };
  } else {
    displaySpecificAttrs = {
      theme: SelectTheme.Select,
      label: displayName,
    };
  }

  const showMenu = props.hideEvents
    ? currentType !== ProfileHistoryType.Latest
    : currentType !== ProfileHistoryType.EventTime;

  return (
    <>
      {showMenu && (
        <div className='subitem-text'>
          <SelectReact
            className='subitem-select'
            {...displaySpecificAttrs}
            menuWidth={null}
            neverShowCaret
            elref='profileHistorySubitem'
            sections={
              ControlBarActionItems[ControlBarAction.ProfileHistory]({
                currentProfileHistoryType: currentType,
                hideEvents: props.hideEvents,
                selectorType: props.selectorType,
              }).subSections
            }
            onChange={(ev: CustomEvent) => {
              props.onTypeChange({...segmentation, profileHistoryOptions: {type: ev.detail.item.value.toType}});
            }}
          />
        </div>
      )}
    </>
  );
}
